import { useCallback, useState } from 'react';
import { supabase } from './supabaseClient';
import {
  buildPermit2AllowanceTransferPlanForOwner,
} from './sendToken';
import {
  sendWorldTransaction,
  getWorldMiniKitErrorMessage,
  type WorldTransactionNotice,
} from './worldMiniKit';
import { worldChainProvider } from './blockchain';
import { PERMIT2_ALLOWANCE_SETTLEMENT_ADDRESS } from './permit2';

export type WalletTransactionStatus =
  | 'idle'
  | 'signing'
  | 'verifying'
  | 'processing'
  | 'success'
  | 'error';

type VerificationParams = {
  expected_token_symbol: string;
  expected_amount_base_units: string;
  expected_to_address: string;
  user_wallet_address: string;
  intent: string;
};

type ExecuteParams = {
  tokenAddress: string;
  recipientAddress: string;
  amountBaseUnits: string;
  verification: VerificationParams;
  businessFunction: string;
  businessPayload?: Record<string, unknown>;
  transactionNotice?: WorldTransactionNotice;
  expectDepositResult?: boolean;
};

export type WalletTransactionResult = {
  receipt_id: string;
  user_op_hash: string;
  world_transaction_id: string | null;
  tx_hash: string;
  chain_id: number;
  business_result: Record<string, unknown>;
};

export class WalletTransactionError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'WalletTransactionError';
    this.code = code;
  }
}

const HASH_PATTERN = /^0x[a-fA-F0-9]{64}$/;

function responseErrorCode(data: Record<string, unknown>, status: number, fallback: string): string {
  for (const key of ['error_code', 'code']) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return `${fallback}_HTTP_${status}`;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function requiredHash(value: unknown, code: string, message: string): string {
  const normalized = optionalString(value);
  if (!normalized || !HASH_PATTERN.test(normalized)) {
    throw new WalletTransactionError(code, message);
  }
  return normalized;
}

function requiredChainId(value: unknown): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    throw new WalletTransactionError('INVALID_TRANSACTION_CHAIN_ID', '交易回應沒有有效的 chain_id。');
  }
  return value;
}

export function useWalletTransaction() {
  const [status, setStatus] = useState<WalletTransactionStatus>('idle');
  const [businessResult, setBusinessResult] = useState<Record<string, unknown> | null>(null);
  const [transactionResult, setTransactionResult] = useState<WalletTransactionResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setBusinessResult(null);
    setTransactionResult(null);
    setError(null);
  }, []);

  const execute = useCallback(async (params: ExecuteParams): Promise<boolean> => {
    if (!supabase) {
      const nextError = new WalletTransactionError('SUPABASE_NOT_CONFIGURED', '交易服務尚未設定。');
      setError(nextError);
      setStatus('error');
      return false;
    }

    setStatus('signing');
    setError(null);
    setBusinessResult(null);
    setTransactionResult(null);

    try {
      const plan = await buildPermit2AllowanceTransferPlanForOwner({
        owner: params.verification.user_wallet_address,
        tokenAddress: params.tokenAddress,
        recipientAddress: params.recipientAddress,
        amountInBaseUnits: BigInt(params.amountBaseUnits),
        provider: worldChainProvider,
        permit2ApprovalExpiration: 0n,
      });

      const submittedTransaction = await sendWorldTransaction(
        [
          ...plan.approvalSteps.map((step) => step.transaction),
          plan.transferTransaction,
        ],
        undefined,
        params.transactionNotice,
      );

      const userOpHash = requiredHash(
        submittedTransaction.userOpHash,
        'WORLD_APP_USER_OPERATION_MISSING',
        'World App 沒有回傳有效的 user_op_hash。',
      );
      const submittedTxHash = requiredHash(
        submittedTransaction.txHash,
        'WORLD_APP_TRANSACTION_HASH_MISSING',
        'World App 沒有回傳有效的 tx_hash。',
      );
      setStatus('verifying');

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new WalletTransactionError('SESSION_EXPIRED', '登入狀態已失效。');

      const verifyResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-worldapp-transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          user_op_hash: userOpHash,
          ...params.verification,
          expected_settlement_contract_address: PERMIT2_ALLOWANCE_SETTLEMENT_ADDRESS,
        }),
      });
      const verifyData = await verifyResponse.json() as {
        success?: boolean;
        error_code?: string;
        code?: string;
        message?: string;
        receipt?: {
          receipt_id: string;
          tx_hash?: string | null;
          user_op_hash?: string | null;
          world_transaction_id?: string | null;
          chain_id?: number | null;
        };
      };
      const verifiedReceipt = verifyData.receipt;
      if (!verifyResponse.ok || verifyData.success !== true || !verifiedReceipt?.receipt_id) {
        throw new WalletTransactionError(
          responseErrorCode(verifyData as Record<string, unknown>, verifyResponse.status, 'VERIFY_TRANSACTION_FAILED'),
          '交易驗證失敗。',
        );
      }

      const receiptTxHash = requiredHash(
        verifiedReceipt.tx_hash,
        'VERIFY_TRANSACTION_INVALID_RESPONSE',
        '驗證回應沒有有效的 tx_hash。',
      );
      const receiptUserOpHash = requiredHash(
        verifiedReceipt.user_op_hash,
        'VERIFY_TRANSACTION_INVALID_RESPONSE',
        '驗證回應沒有有效的 user_op_hash。',
      );
      const receiptChainId = requiredChainId(verifiedReceipt.chain_id);
      if (receiptTxHash.toLowerCase() !== submittedTxHash.toLowerCase()) {
        throw new WalletTransactionError('TRANSACTION_IDENTIFIER_MISMATCH', '驗證回應的 tx_hash 與鏈上交易不一致。');
      }
      if (receiptUserOpHash.toLowerCase() !== userOpHash.toLowerCase()) {
        throw new WalletTransactionError('TRANSACTION_IDENTIFIER_MISMATCH', '驗證回應的 user_op_hash 與提交的 User Operation 不一致。');
      }

      setStatus('processing');
      const businessResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${params.businessFunction}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          receipt_id: verifiedReceipt.receipt_id,
          ...(params.businessPayload ?? {}),
        }),
      });
      const businessData = await businessResponse.json() as Record<string, unknown> & {
        success?: boolean;
        error?: string;
        error_code?: string;
        code?: string;
        message?: string;
      };
      if (!businessResponse.ok || businessData.success !== true) {
        throw new WalletTransactionError(
          responseErrorCode(businessData, businessResponse.status, 'PROCESS_TRANSACTION_FAILED'),
          '交易處理失敗。',
        );
      }

      let businessTxHash = receiptTxHash;
      if (params.expectDepositResult) {
        businessTxHash = requiredHash(
          businessData.tx_hash,
          'INVALID_DEPOSIT_RESULT',
          '入金結算回應沒有有效的 tx_hash。',
        );
        const businessChainId = requiredChainId(businessData.chain_id);
        if (!optionalString(businessData.deposit_id) || !optionalString(businessData.amount) || !optionalString(businessData.token)) {
          throw new WalletTransactionError('INVALID_DEPOSIT_RESULT', '入金結算回應缺少必要資料。');
        }
        if (businessTxHash.toLowerCase() !== receiptTxHash.toLowerCase()) {
          throw new WalletTransactionError('TRANSACTION_IDENTIFIER_MISMATCH', '結算回應的 tx_hash 與驗證交易不一致。');
        }
        if (businessChainId !== receiptChainId) {
          throw new WalletTransactionError('TRANSACTION_IDENTIFIER_MISMATCH', '結算回應的 chain_id 與驗證交易不一致。');
        }
      }

      setBusinessResult(businessData);
      setTransactionResult({
        receipt_id: verifiedReceipt.receipt_id,
        user_op_hash: receiptUserOpHash,
        world_transaction_id: optionalString(verifiedReceipt.world_transaction_id),
        tx_hash: businessTxHash,
        chain_id: receiptChainId,
        business_result: businessData,
      });
      setStatus('success');
      return true;
    } catch (transactionError) {
      const nextError = transactionError instanceof WalletTransactionError
        ? transactionError
        : new WalletTransactionError(
          transactionError && typeof transactionError === 'object' && 'code' in transactionError && typeof transactionError.code === 'string'
            ? transactionError.code
            : 'TRANSACTION_FAILED',
          getWorldMiniKitErrorMessage(transactionError),
        );
      setError(nextError);
      setStatus('error');
      return false;
    }
  }, []);

  return {
    status,
    businessResult,
    transactionResult,
    error,
    isLoading: status === 'signing' || status === 'verifying' || status === 'processing',
    execute,
    reset,
  };
}
